/* eslint-disable max-lines */
/* eslint-disable consistent-return */
/* eslint-disable max-statements */
import React, { useMemo, useEffect } from 'react';
import {
  useMemoMergePropsWithDefault,
  useValidateTypescriptPropTypes,
  describe,
  type ValidationMap,
} from '@elliemae/ds-props-helpers';
import { useHeadlessTooltip } from '@elliemae/ds-hooks-headless-tooltip';
import type { DSHookFloatingContextT } from './react-desc-prop-types.js';
import { defaultProps, DSFloatingContextPropTypes } from './react-desc-prop-types.js';
import { useComputedPositionStyles } from './useComputedPositionStyles.js';
import { usePositionObserver } from './utils/positionObserver.js';

const useFloatingContext = (props: DSHookFloatingContextT.Props = {}) => {
  const propsWithDefault = useMemoMergePropsWithDefault<DSHookFloatingContextT.InternalProps>(props, defaultProps);
  useValidateTypescriptPropTypes(propsWithDefault, DSFloatingContextPropTypes, 'FloatingContext');

  const {
    withoutPortal,
    withoutAnimation,
    portalDOMContainer,
    animationDuration,
    placement,
    customOffset,
    placementOrderPreference,
    onOpen,
    onClose,
    externallyControlledIsOpen,
  } = propsWithDefault;

  const [internalIsOpen, setInternalIsOpen] = React.useState<boolean>(false);
  const isOpen = useMemo(
    () => (externallyControlledIsOpen !== undefined ? externallyControlledIsOpen : internalIsOpen),
    [externallyControlledIsOpen, internalIsOpen],
  );

  const handleOpen = React.useCallback(() => {
    setInternalIsOpen(true);
    onOpen?.();
  }, [onOpen]);

  const handleClose = React.useCallback(() => {
    setInternalIsOpen(false);
    onClose?.();
  }, [onClose]);

  const tooltipHelpers = useHeadlessTooltip({ onOpen: handleOpen, onClose: handleClose });
  const { setReferenceElement, referenceElement, hideTooltip, showTooltip } = tooltipHelpers;

  // Anchor and floating refs
  const [floating, setFloating] = React.useState<HTMLElement | null>(null);

  // Compute position (skip when closed)
  const { arrowStyles, floatingStyles, hasComputedOnce, updateStyles, debouncedUpdateStyles, mutableUpdateStyles } =
    useComputedPositionStyles({
      reference: referenceElement,
      floating,
      placement,
      placementOrderPreference,
      customOffset,
      withoutPortal,
      preventComputing: !isOpen,
      debounceMs: 150,
    });

  useEffect(() => {
    if (!isOpen) return;

    const observers: ResizeObserver[] = [];

    if (floating) {
      const roFloating = new ResizeObserver(() => {
        mutableUpdateStyles.current();
      });
      roFloating.observe(floating);
      observers.push(roFloating);
    }

    return () => observers.forEach((o) => o.disconnect());
  }, [isOpen, floating, updateStyles, mutableUpdateStyles]);

  const onObservedMovement = React.useCallback(() => {
    if (!isOpen) return;
    debouncedUpdateStyles();
  }, [isOpen, debouncedUpdateStyles]);
  usePositionObserver(referenceElement, onObservedMovement);

  // Expose visibility: only visible if open and already computed at least once
  const computedFloatingStyles = useMemo<React.CSSProperties>(
    () => ({
      ...floatingStyles,
      visibility: isOpen && hasComputedOnce ? 'visible' : 'hidden',
    }),
    [floatingStyles, hasComputedOnce, isOpen],
  );

  const refs = useMemo(
    () => ({
      setReference: setReferenceElement,
      setFloating,
      floating,
      reference: referenceElement,
    }),
    [setReferenceElement, floating, referenceElement],
  );

  const handlers = useMemo(
    () => ({
      onMouseEnter: tooltipHelpers.onMouseEnter,
      onMouseLeave: tooltipHelpers.onMouseLeave,
      onFocus: tooltipHelpers.onFocus,
      onBlur: tooltipHelpers.onBlur,
    }),
    [tooltipHelpers.onBlur, tooltipHelpers.onFocus, tooltipHelpers.onMouseEnter, tooltipHelpers.onMouseLeave],
  );

  return useMemo(
    () => ({
      refs,
      floatingStyles: computedFloatingStyles,
      handlers,
      isOpen,
      arrowStyles,
      hideTooltip,
      showTooltip,
      context: {
        withoutPortal,
        withoutAnimation,
        portalDOMContainer,
        animationDuration,
      },
      mutableUpdateStyles,
      forceUpdatePosition: updateStyles,
    }),
    [
      refs,
      computedFloatingStyles,
      handlers,
      isOpen,
      arrowStyles,
      hideTooltip,
      showTooltip,
      withoutPortal,
      withoutAnimation,
      portalDOMContainer,
      animationDuration,
      mutableUpdateStyles,
      updateStyles,
    ],
  );
};

useFloatingContext.displayName = 'FloatingContext';
const UseFloatingContextWithSchema = describe(useFloatingContext);
UseFloatingContextWithSchema.propTypes =
  DSFloatingContextPropTypes as unknown as ValidationMap<DSHookFloatingContextT.Props>;

export { useFloatingContext, UseFloatingContextWithSchema };
